import { trpc } from '@/config/trpc'
import { toast } from 'sonner'
import { startAuthProcess as beginLinkedInAuth } from '@/lib/auth/linkedin'
import { startAuthProcess as beginInstagramAuth } from '@/lib/auth/instagram'
import { startAuthProcess as beginTikTokAuth } from '@/lib/auth/tiktok'
import { startAuthProcess as beginFacebookAuth } from '@/lib/auth/facebook'
import { AccountTabSocialMediaConnection } from '@/components/account/account-tab/account-tab-social-media-connection'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/shadcn/card'
import { type PublishablePlatformType } from '@backend/lib/db/schema/platform'

export function AccountSocialMediaPage() {
  const { data: linkedinData, isLoading: isLinkedinLoading } =
    trpc.platform.isAuthenticated.useQuery({
      platform: 'linkedin',
    })
  const { data: instagramData, isLoading: isInstagramLoading } =
    trpc.platform.isAuthenticated.useQuery({
      platform: 'instagram',
    })
  const { data: tiktokData, isLoading: isTikTokLoading } = trpc.platform.isAuthenticated.useQuery({
    platform: 'tiktok',
  })
  const { data: youtubeData, isLoading: isYouTubeLoading } = trpc.platform.isAuthenticated.useQuery(
    { platform: 'youtube' },
  )
  const { data: facebookData, isLoading: isFacebookLoading } =
    trpc.platform.isAuthenticated.useQuery({ platform: 'facebook' })

  const utils = trpc.useUtils()

  const { mutateAsync: signOut } = trpc.platform.signOut.useMutation()
  const { mutateAsync: setAutoImport } = trpc.import.setAutoImport.useMutation()
  const { mutateAsync: removeAccount } = trpc.platform.removeAccount.useMutation()

  const handleToggleAutoImport = async (
    platform: PublishablePlatformType,
    accountId: string,
    enabled: boolean,
  ) => {
    try {
      await setAutoImport({ platform, accountId, autoImport: enabled })
      utils.platform.isAuthenticated.invalidate({ platform })
    } catch {
      toast.error('Failed to update auto-import setting')
    }
  }

  const handleRemoveAccount = async (platform: PublishablePlatformType, accountId: string) => {
    try {
      await removeAccount({ platform, accountId })
      utils.platform.isAuthenticated.invalidate({ platform })
      utils.analytics.overview.invalidate()
      utils.analytics.accountOverview.invalidate()
      toast.success('Account removed')
    } catch {
      toast.error('Failed to remove account')
    }
  }

  const handleSignOut = async (platform: PublishablePlatformType) => {
    const previousData = utils.platform.isAuthenticated.getData({ platform })
    utils.platform.isAuthenticated.setData({ platform }, { isAuthenticated: false })
    try {
      await signOut({ platform })
    } catch (error) {
      if (previousData) {
        utils.platform.isAuthenticated.setData({ platform }, previousData)
      }
      console.error('Sign out failed:', error)
    } finally {
      utils.platform.isAuthenticated.invalidate({ platform })
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-xl sm:text-2xl">Social Media Accounts</CardTitle>
        <CardDescription>Connect and manage your social media accounts</CardDescription>
      </CardHeader>
      <CardContent>
        <AccountTabSocialMediaConnection
          platform="linkedin"
          isLoading={isLinkedinLoading}
          accounts={linkedinData?.isAuthenticated === true ? linkedinData.accounts : undefined}
          expiresAt={linkedinData?.isAuthenticated === true ? linkedinData.expiresAt : undefined}
          refreshTokenExpiresAt={
            linkedinData?.isAuthenticated === true && 'refreshTokenExpiresAt' in linkedinData
              ? linkedinData.refreshTokenExpiresAt
              : undefined
          }
          scopes={linkedinData?.isAuthenticated === true ? linkedinData.scopes : undefined}
          analyticsAvailable={
            linkedinData?.isAuthenticated === true ? linkedinData.analyticsAvailable : undefined
          }
          syncStatus={linkedinData?.isAuthenticated === true ? linkedinData.syncStatus : undefined}
          syncStatusDetail={
            linkedinData?.isAuthenticated === true ? linkedinData.syncStatusDetail : undefined
          }
          syncStatusAt={
            linkedinData?.isAuthenticated === true ? linkedinData.syncStatusAt : undefined
          }
          onStartAuth={() => beginLinkedInAuth({ referrerPath: window.location.pathname })}
          onSignOut={() => handleSignOut('linkedin')}
          onToggleAutoImport={(accountId, enabled) =>
            handleToggleAutoImport('linkedin', accountId, enabled)
          }
          onRemoveAccount={(accountId) => handleRemoveAccount('linkedin', accountId)}
        />

        <AccountTabSocialMediaConnection
          platform="instagram"
          isLoading={isInstagramLoading}
          accounts={instagramData?.isAuthenticated === true ? instagramData.accounts : undefined}
          expiresAt={instagramData?.isAuthenticated === true ? instagramData.expiresAt : undefined}
          scopes={instagramData?.isAuthenticated === true ? instagramData.scopes : undefined}
          analyticsAvailable={
            instagramData?.isAuthenticated === true ? instagramData.analyticsAvailable : undefined
          }
          syncStatus={
            instagramData?.isAuthenticated === true ? instagramData.syncStatus : undefined
          }
          syncStatusDetail={
            instagramData?.isAuthenticated === true ? instagramData.syncStatusDetail : undefined
          }
          syncStatusAt={
            instagramData?.isAuthenticated === true ? instagramData.syncStatusAt : undefined
          }
          onStartAuth={() => beginInstagramAuth({ referrerPath: window.location.pathname })}
          onSignOut={() => handleSignOut('instagram')}
          onToggleAutoImport={(accountId, enabled) =>
            handleToggleAutoImport('instagram', accountId, enabled)
          }
          onRemoveAccount={(accountId) => handleRemoveAccount('instagram', accountId)}
        />

        <AccountTabSocialMediaConnection
          platform="tiktok"
          isLoading={isTikTokLoading}
          accounts={tiktokData?.isAuthenticated === true ? tiktokData.accounts : undefined}
          expiresAt={tiktokData?.isAuthenticated === true ? tiktokData.expiresAt : undefined}
          scopes={tiktokData?.isAuthenticated === true ? tiktokData.scopes : undefined}
          analyticsAvailable={
            tiktokData?.isAuthenticated === true ? tiktokData.analyticsAvailable : undefined
          }
          syncStatus={tiktokData?.isAuthenticated === true ? tiktokData.syncStatus : undefined}
          syncStatusDetail={
            tiktokData?.isAuthenticated === true ? tiktokData.syncStatusDetail : undefined
          }
          syncStatusAt={tiktokData?.isAuthenticated === true ? tiktokData.syncStatusAt : undefined}
          onStartAuth={() => beginTikTokAuth({ referrerPath: window.location.pathname })}
          onSignOut={() => handleSignOut('tiktok')}
          onToggleAutoImport={(accountId, enabled) =>
            handleToggleAutoImport('tiktok', accountId, enabled)
          }
          onRemoveAccount={(accountId) => handleRemoveAccount('tiktok', accountId)}
        />
        <AccountTabSocialMediaConnection
          platform="youtube"
          isLoading={isYouTubeLoading}
          accounts={youtubeData?.isAuthenticated === true ? youtubeData.accounts : undefined}
          expiresAt={youtubeData?.isAuthenticated === true ? youtubeData.expiresAt : undefined}
          scopes={youtubeData?.isAuthenticated === true ? youtubeData.scopes : undefined}
          analyticsAvailable={
            youtubeData?.isAuthenticated === true ? youtubeData.analyticsAvailable : undefined
          }
          syncStatus={youtubeData?.isAuthenticated === true ? youtubeData.syncStatus : undefined}
          syncStatusDetail={
            youtubeData?.isAuthenticated === true ? youtubeData.syncStatusDetail : undefined
          }
          syncStatusAt={
            youtubeData?.isAuthenticated === true ? youtubeData.syncStatusAt : undefined
          }
          onSignOut={() => handleSignOut('youtube')}
          onToggleAutoImport={(accountId, enabled) =>
            handleToggleAutoImport('youtube', accountId, enabled)
          }
          onRemoveAccount={(accountId) => handleRemoveAccount('youtube', accountId)}
        />

        <AccountTabSocialMediaConnection
          platform="facebook"
          isLoading={isFacebookLoading}
          accounts={facebookData?.isAuthenticated === true ? facebookData.accounts : undefined}
          expiresAt={facebookData?.isAuthenticated === true ? facebookData.expiresAt : undefined}
          scopes={facebookData?.isAuthenticated === true ? facebookData.scopes : undefined}
          analyticsAvailable={
            facebookData?.isAuthenticated === true ? facebookData.analyticsAvailable : undefined
          }
          syncStatus={facebookData?.isAuthenticated === true ? facebookData.syncStatus : undefined}
          syncStatusDetail={
            facebookData?.isAuthenticated === true ? facebookData.syncStatusDetail : undefined
          }
          syncStatusAt={
            facebookData?.isAuthenticated === true ? facebookData.syncStatusAt : undefined
          }
          onStartAuth={() => {
            // The Facebook auth flow throws synchronously if VITE_FACEBOOK_CLIENT_ID
            // / VITE_FACEBOOK_CONFIG_ID are unset. React doesn't catch errors thrown
            // from event handlers, so without this try/catch the button would just
            // appear broken. Surface a toast instead.
            try {
              beginFacebookAuth({ referrerPath: window.location.pathname })
            } catch (error) {
              toast.error('Facebook is not configured for this environment', {
                description: error instanceof Error ? error.message : undefined,
              })
            }
          }}
          onSignOut={() => handleSignOut('facebook')}
          onToggleAutoImport={(accountId, enabled) =>
            handleToggleAutoImport('facebook', accountId, enabled)
          }
          onRemoveAccount={(accountId) => handleRemoveAccount('facebook', accountId)}
        />
      </CardContent>
    </Card>
  )
}
